import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Phone, Globe, Share2, TrendingUp, Palette, Code } from 'lucide-react';
import { services, companyInfo } from '../data/mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CTABanner from '../components/CTABanner';
import SEO from '../components/SEO';

const iconMap = {
  Globe: Globe,
  Share2: Share2,
  TrendingUp: TrendingUp,
  Palette: Palette,
  Code: Code,
};

const colorMap = {
  'mid-blue': { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/30', solid: 'bg-cyan-500' },
  'mid-purple': { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30', solid: 'bg-purple-500' },
  'mid-orange': { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30', solid: 'bg-orange-500' },
  'mid-pink': { bg: 'bg-pink-500/10', text: 'text-pink-400', border: 'border-pink-500/30', solid: 'bg-pink-500' },
  'mid-green': { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', solid: 'bg-emerald-500' },
};

const ServicePage = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const Icon = iconMap[service.icon];
  const colors = colorMap[service.color];
  const otherServices = services.filter(s => s.id !== service.id);

  return (
    <div className="min-h-screen bg-black">
      <SEO
        title={`${service.title} | Infinity Solutions Hub`}
        description={service.fullDescription}
        keywords={[service.title, ...service.features, 'Las Vegas', 'Infinity Solutions Hub'].join(', ')}
        canonical={`https://infinitysolutionshub.com/services/${service.slug}`}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="container mx-auto px-4 lg:px-8">
          <Link 
            to="/#services" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-400 transition-colors mb-8"
          >
            <ArrowLeft size={18} />
            <span className="font-mono text-sm">Back to Services</span>
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className={`inline-flex p-4 rounded-xl ${colors.bg} ${colors.text} mb-6`}>
                <Icon size={40} />
              </div>
              
              <h1 className="hero-heading text-white mb-6">
                {service.title}
              </h1>
              
              <p className="body-large text-white/70 mb-8">
                {service.fullDescription}
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {service.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className={`service-button ${colors.bg} ${colors.text} ${colors.border}`}
                  >
                    {feature}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/#contact"
                  className="cta-button bg-cyan-500 text-black border-cyan-500 hover:bg-cyan-400"
                >
                  Get Free Quote
                </Link>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="cta-button bg-transparent text-white border-white/30 hover:border-white/60 flex items-center gap-2"
                >
                  <Phone size={16} />
                  Call for 20% OFF
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className={`${colors.bg} ${colors.border} border rounded-2xl p-8`}>
                <h3 className="card-heading text-white mb-6">Why Choose Us?</h3>
                <ul className="space-y-4">
                  {service.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check size={20} className={`${colors.text} flex-shrink-0 mt-0.5`} />
                      <span className="text-white/80">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white/5">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className={`service-button ${colors.bg} ${colors.text} ${colors.border} mb-4 inline-block`}>
              Our Process
            </span>
            <h2 className="section-heading text-white mt-4">
              How We Deliver <span className={colors.text}>Results</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.process.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative"
              >
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 h-full">
                  <div className={`w-12 h-12 rounded-full ${colors.solid} text-black flex items-center justify-center font-bold text-lg mb-4`}>
                    {step.step}
                  </div>
                  <h3 className="card-heading text-white mb-2">{step.title}</h3>
                  <p className="text-white/60">{step.desc}</p>
                </div>
                {idx < service.process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight size={20} className="text-white/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner />

      {/* Other Services */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="section-heading text-white mb-12">
            Explore Other <span className="text-cyan-400">Services</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {otherServices.map((s) => {
              const SIcon = iconMap[s.icon];
              const sColors = colorMap[s.color];
              
              return (
                <Link
                  key={s.id}
                  to={`/services/${s.slug}`}
                  className={`group ${sColors.bg} ${sColors.border} border rounded-xl p-6 hover:bg-opacity-20 transition-all`}
                >
                  <div className={`p-3 rounded-lg ${sColors.bg} ${sColors.text} inline-block mb-4`}>
                    <SIcon size={24} />
                  </div>
                  <h3 className="card-heading text-white mb-2 group-hover:underline">{s.title}</h3>
                  <p className="text-white/60 text-sm">{s.description}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePage;
